import { transferXYToDistance, translateAngleToXY } from '@/utils/math-kit';
import { type IVectorEffectReflection, type IVectorEffectShadow, type IVectorLayerProps } from '@/version-types';
import { SHADOW_SIZE } from '../constants';

export const checkIsUsingShadow = (layerData: IVectorLayerProps) => {
    const { effect } = layerData;
    if (!effect) return false;

    const { shadow } = effect;
    if (!shadow || shadow.length === 0) return false;

    return shadow[0].hidden === false;
};

export const checkIsUsingReflection = (layerData: IVectorLayerProps) => {
    const { effect } = layerData;
    if (!effect) return false;

    const { reflection } = effect;
    if (!reflection || reflection.length === 0) return false;

    return reflection[0].hidden === false;
};

export const checkIsUsingBlur = (layerData: IVectorLayerProps) => {
    const { blur } = layerData;
    if (!blur) return false;

    return blur > 0;
};

export const checkIsUsingEffect = (layerData: IVectorLayerProps, options = { includeBlur: true }) => {
    return (
        checkIsUsingShadow(layerData) ||
        checkIsUsingReflection(layerData) ||
        (options.includeBlur && checkIsUsingBlur(layerData))
    );
};

export const getBlurOrReflectionParams = (val: number, type: 'shadow' | 'reflection' | 'stroke') => {
    const baseValue = Math.floor(val / 6);
    const params = {
        shadow: { XY: `-${(baseValue + 1) * 25}%`, WH: `${baseValue * 50 + 150}%` },
        reflection: {
            XY: `-${(baseValue + 1) * 50}%`,
            WH: `${baseValue * 50 + 300}%`,
        },
    };
    return params[type];
};

/**
 * 换算虚拟的距离
 *
 * @param x - 水平坐标
 * @param y - 垂直坐标
 * @param width - 图层宽
 */
export const calculateVirtualDistance = (x: number, y: number, width: number) => {
    return Math.round((transferXYToDistance(x, y) * 100) / width / SHADOW_SIZE);
};

/**
 * 换算真实的距离
 *
 * @param distance - 转换的数值
 * @param width - 图层宽
 */
export const calculateRealDistance = (distance: number, width: number) => {
    return (distance / 100) * width * SHADOW_SIZE;
};

/**
 * 创建默认阴影
 *
 * @param layerWidth - 图层宽度
 */
export const createDefaultShadow = (layerWidth: number) => {
    const defaultDistance = 30;
    const defaultAngle = -45;
    const relDistance = calculateRealDistance(defaultDistance, layerWidth);
    const { x, y } = translateAngleToXY(defaultAngle, relDistance);

    const shadow: IVectorEffectShadow = {
        x,
        y,
        hidden: false,
        blur: 20,
        color: '#27272a',
        opacity: 0.3,
    };

    return shadow;
};

/**
 * 创建默认倒影
 */
export const createDefaultReflection = () => {
    const reflection: IVectorEffectReflection = {
        blur: 5,
        hidden: false,
        y: 0,
        opacity: 0.7,
        size: 100,
    };

    return reflection;
};
